import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  Chip,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ArticleIcon from '@mui/icons-material/Article';
import { useHomeState } from '../context/home-context';
import { useNavigate } from '../../../hooks/useNavigate';
import HomeSection, { HomeSectionVariant } from '../../components/home/home-section';
import { staticCardSx } from '../../../theme/tokens';

const listRowSx = {
  minHeight: 56,
  borderRadius: 1,
  '&:hover': {
    backgroundColor: 'brand.surface',
    '& .MuiListItemText-primary': { color: 'brand.text' },
  },
};

interface PanelCardProps {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

/** Bloco independente (Diário Oficial ou Publicações) com cabeçalho próprio, para não parecer aba. */
function PanelCard({ id, title, description, icon, children }: PanelCardProps) {
  const headingId = `${id}-titulo`;

  return (
    <Box
      component="article"
      aria-labelledby={headingId}
      sx={{
        ...(staticCardSx as object),
        p: { xs: 2, md: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <Box
          aria-hidden
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 2,
            backgroundColor: 'brand.surface',
            color: 'brand.text',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h3" component="h3" id={headingId} sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Box>
  );
}

interface SectionPublicationsTabsProps {
  title?: string;
  subtitle?: string;
  variant?: HomeSectionVariant;
}

export default function SectionPublicationsTabs({
  title,
  subtitle,
  variant = 'subtle',
}: SectionPublicationsTabsProps = {}) {
  const { groupedDailyOfficial, categoriesWithSubcategories } = useHomeState();
  const navigate = useNavigate();
  const [searchYear, setSearchYear] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);

  // Filtrar anos baseado na busca
  const filteredYears = useMemo(() => {
    if (!groupedDailyOfficial || groupedDailyOfficial.length === 0) {
      return [];
    }
    if (!searchYear.trim()) {
      return groupedDailyOfficial;
    }
    return groupedDailyOfficial.filter((item) => item.year?.toString().includes(searchYear.trim()));
  }, [groupedDailyOfficial, searchYear]);

  // Filtrar categorias baseado na busca
  const filteredCategories = useMemo(() => {
    if (!categoriesWithSubcategories || categoriesWithSubcategories.length === 0) {
      return [];
    }
    if (!searchCategory.trim()) {
      return categoriesWithSubcategories;
    }
    const searchLower = searchCategory.trim().toLowerCase();
    return categoriesWithSubcategories.filter((category) => {
      const categoryMatch = category.name?.toLowerCase().includes(searchLower);
      const subcategoryMatch = category.subcategories?.some((sub) => sub.name?.toLowerCase().includes(searchLower));
      return categoryMatch || subcategoryMatch;
    });
  }, [categoriesWithSubcategories, searchCategory]);

  const handleCategoryClick = (categoryId: number) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const handleCategoryNavigate = (categoryId: number) => {
    navigate(`/publicacoes/${categoryId}`);
  };

  const handleSubcategoryNavigate = (categoryId: number, subcategoryId: number) => {
    navigate(`/publicacoes/${categoryId}/${subcategoryId}`);
  };

  const hasDailyOfficial = !!groupedDailyOfficial?.length;
  const hasPublications = !!categoriesWithSubcategories?.length;

  // Sem dados em nenhum dos blocos, não renderiza
  if (!hasDailyOfficial && !hasPublications) {
    return null;
  }

  return (
    <HomeSection
      id="publicacoes-diario"
      variant={variant}
      title={title || 'Diário Oficial e publicações'}
      subtitle={subtitle || 'Consulte os diários oficiais por ano ou as publicações por categoria.'}
    >
      {/* Dois blocos lado a lado (empilhados no mobile); com só um deles, ocupa a largura toda */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: hasDailyOfficial && hasPublications ? 'repeat(2, minmax(0, 1fr))' : '1fr',
          },
          gap: { xs: 2, md: 3 },
          alignItems: 'stretch',
        }}
      >
        {hasDailyOfficial && (
          <PanelCard
            id="diario-oficial-home"
            title="Diário Oficial"
            description="Edições do diário oficial do município, organizadas por ano."
            icon={<DescriptionIcon />}
          >
            <TextField
              fullWidth
              label="Filtrar por ano"
              placeholder="Ex.: 2024"
              inputMode="numeric"
              value={searchYear}
              onChange={(e) => setSearchYear(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ mb: 2 }}
            />

            <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
              {filteredYears.length > 0 ? (
                filteredYears.map((item, index) => (
                  <React.Fragment key={item.year}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => navigate(`/diario-oficial/${item.year}`)} sx={listRowSx}>
                        <ListItemIcon sx={{ color: 'brand.text' }}>
                          <DescriptionIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Diário Oficial - ${item.year}`}
                          secondary={`${item.quantity || 0} publicações`}
                          primaryTypographyProps={{
                            fontWeight: 600,
                            sx: { fontVariantNumeric: 'tabular-nums' },
                          }}
                          secondaryTypographyProps={{
                            sx: { fontVariantNumeric: 'tabular-nums' },
                          }}
                        />
                        <ArrowForwardIcon sx={{ color: 'text.secondary' }} />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredYears.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhum ano encontrado para "{searchYear}"
                  </Typography>
                </Box>
              )}
            </List>

            {filteredYears.length > 0 && (
              <Box sx={{ mt: 'auto' }}>
                <Divider />
                <Box sx={{ pt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate('/diario-oficial')}
                  >
                    Ver todos os diários oficiais
                  </Button>
                </Box>
              </Box>
            )}
          </PanelCard>
        )}

        {hasPublications && (
          <PanelCard
            id="publicacoes-home"
            title="Publicações"
            description="Atos e documentos publicados, organizados por categoria."
            icon={<ArticleIcon />}
          >
            <TextField
              fullWidth
              label="Filtrar por categoria ou subcategoria"
              value={searchCategory}
              onChange={(e) => setSearchCategory(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ mb: 2 }}
            />

            <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <React.Fragment key={category.id}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleCategoryClick(category.id)} sx={listRowSx}>
                        <ListItemIcon>
                          <FolderIcon sx={{ color: 'brand.text' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Typography variant="body1" fontWeight={600}>
                                {category.name}
                              </Typography>
                              <Chip
                                label={category.publicationCount || 0}
                                size="small"
                                color="primary"
                                sx={{ minWidth: 40 }}
                              />
                            </Box>
                          }
                          secondary={
                            category.subcategories && category.subcategories.length > 0
                              ? `${category.subcategories.length} subcategorias`
                              : 'Sem subcategorias'
                          }
                        />
                        {category.subcategories && category.subcategories.length > 0 ? (
                          expandedCategories.includes(category.id) ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )
                        ) : (
                          <ArrowForwardIcon
                            sx={{ color: 'text.secondary' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategoryNavigate(category.id);
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>

                    {/* Subcategorias */}
                    {category.subcategories && category.subcategories.length > 0 && (
                      <Collapse in={expandedCategories.includes(category.id)} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {category.subcategories.map((subcategory) => (
                            <ListItem key={subcategory.id} disablePadding>
                              <ListItemButton
                                sx={{ ...listRowSx, pl: 8 }}
                                onClick={() => handleSubcategoryNavigate(category.id, subcategory.id)}
                              >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <ArticleIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={subcategory.name}
                                  secondary={`${subcategory.publicationCount || 0} publicações`}
                                />
                                <ArrowForwardIcon sx={{ color: 'text.secondary' }} />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    )}

                    <Divider />
                  </React.Fragment>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma categoria encontrada para "{searchCategory}"
                  </Typography>
                </Box>
              )}
            </List>

            {filteredCategories.length > 0 && (
              <Box sx={{ mt: 'auto' }}>
                <Divider />
                <Box sx={{ pt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate('/publicacoes')}
                  >
                    Ver todas as publicações
                  </Button>
                </Box>
              </Box>
            )}
          </PanelCard>
        )}
      </Box>
    </HomeSection>
  );
}
